import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, CheckCircle, Circle, Pause, Eye } from "@phosphor-icons/react";

const COLUMNS = [
  { id: "todo", label: "To do", icon: Circle },
  { id: "in_progress", label: "In progress", icon: Pause },
  { id: "review", label: "Review", icon: Eye },
  { id: "done", label: "Done", icon: CheckCircle },
];

const PRIO_TONE = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-blue-500/15 text-blue-400",
  high: "bg-amber-500/15 text-amber-400",
  urgent: "bg-rose-500/15 text-rose-400",
};

const EMPTY = { title: "", description: "", status: "todo", priority: "medium", due_date: "", assignee: "" };

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/tasks");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title) return;
    await api.post("/tasks", form);
    setOpen(false);
    setForm(EMPTY);
    load();
  };

  const setStatus = async (t, status) => {
    setItems((prev) => prev.map((x) => x.id === t.id ? { ...x, status } : x));
    await api.patch(`/tasks/${t.id}/status`, { status });
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline mb-2">— Execution</div>
          <h1 className="font-display text-4xl tracking-tight leading-none">Tasks</h1>
          <p className="text-muted-foreground text-sm mt-2">Kanban view of everything on the team's plate.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary text-primary-foreground" data-testid="new-task-btn">
              <Plus size={14} className="mr-1.5" /> New task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display tracking-tight">New task</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2 space-y-1.5"><Label className="overline">Title *</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
              <div className="col-span-2 space-y-1.5"><Label className="overline">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} /></div>
              <div className="space-y-1.5"><Label className="overline">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["low","medium","high","urgent"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="overline">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COLUMNS.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1.5"><Label className="overline">Due date</Label><Input type="datetime-local" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} /></div>
              <div className="col-span-2 space-y-1.5"><Label className="overline">Assignee</Label><Input value={form.assignee} onChange={(e) => setForm({...form, assignee: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="task-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const list = items.filter((t) => t.status === col.id);
          return (
            <div key={col.id} className="rounded-xl border border-border bg-card/60 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <col.icon size={14} weight="duotone" className="text-primary" />
                  <span className="text-sm font-medium">{col.label}</span>
                </div>
                <span className="text-xs text-muted-foreground">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.map((t) => (
                  <div key={t.id} className="rounded-lg border border-border bg-background p-3 hover:border-primary/40 transition-colors" data-testid={`task-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium leading-snug">{t.title}</div>
                      <Badge variant="secondary" className={`text-[10px] uppercase tracking-wider ${PRIO_TONE[t.priority]}`}>{t.priority}</Badge>
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{t.description}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[11px] text-muted-foreground">{t.due_date ? new Date(t.due_date).toLocaleDateString() : ""}</span>
                      <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                        <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLUMNS.map(c => <SelectItem key={c.id} value={c.id} className="text-xs">{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {list.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">Nothing here.</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
